import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Server, Shield, TrendingUp, DollarSign, Play, Eye, Mail, Users, Calendar, ShoppingBag, BarChart3, Radio, Music, ExternalLink, Plus, Send, Image, Clock, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
interface CreatorDashboardProps {
  onBack?: () => void;
}
const trafficData = [{
  name: 'Mon',
  views: 2400,
  streams: 1800
}, {
  name: 'Tue',
  views: 1398,
  streams: 2200
}, {
  name: 'Wed',
  views: 5800,
  streams: 3200
}, {
  name: 'Thu',
  views: 3908,
  streams: 2800
}, {
  name: 'Fri',
  views: 4800,
  streams: 4100
}, {
  name: 'Sat',
  views: 7800,
  streams: 5200
}, {
  name: 'Sun',
  views: 6200,
  streams: 4800
}];
const revenueData = [{
  name: 'Jan',
  streaming: 1200,
  store: 800,
  ads: 400
}, {
  name: 'Feb',
  streaming: 1800,
  store: 1200,
  ads: 600
}, {
  name: 'Mar',
  streaming: 2400,
  store: 900,
  ads: 800
}, {
  name: 'Apr',
  streaming: 3200,
  store: 1500,
  ads: 1000
}, {
  name: 'May',
  streaming: 2800,
  store: 2100,
  ads: 1200
}, {
  name: 'Jun',
  streaming: 4100,
  store: 2800,
  ads: 1400
}];
const emailStats = [{
  name: 'Sent',
  value: 12400
}, {
  name: 'Opened',
  value: 8200
}, {
  name: 'Clicked',
  value: 3100
}];
const scheduledPosts = [{
  platform: 'Instagram',
  content: 'New single announcement 🎵',
  time: 'Today, 6:00 PM',
  status: 'scheduled'
}, {
  platform: 'Twitter/X',
  content: 'Behind the scenes studio session',
  time: 'Tomorrow, 12:00 PM',
  status: 'scheduled'
}, {
  platform: 'TikTok',
  content: 'Dance challenge clip',
  time: 'Wed, 3:00 PM',
  status: 'draft'
}, {
  platform: 'YouTube',
  content: 'Full music video premiere',
  time: 'Fri, 8:00 PM',
  status: 'scheduled'
}];
const storeProducts = [{
  name: 'Limited Edition Hoodie',
  price: 65,
  sold: 142,
  stock: 58
}, {
  name: 'Signed Vinyl LP',
  price: 45,
  sold: 89,
  stock: 11
}, {
  name: 'VIP Meet & Greet Pass',
  price: 150,
  sold: 24,
  stock: 6
}];
const COLORS = ['#FFD700', '#FF0000', '#4F46E5'];
export function CreatorDashboard({
  onBack
}: CreatorDashboardProps) {
  const [activeSection, setActiveSection] = useState('overview');
  const sections = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'hosting',
    label: 'Hosting & Domains'
  }, {
    id: 'streaming',
    label: 'Streaming'
  }, {
    id: 'content',
    label: 'Content & Traffic'
  }, {
    id: 'email',
    label: 'Email & CRM'
  }, {
    id: 'social',
    label: 'Social Scheduler'
  }, {
    id: 'store',
    label: 'E-Commerce'
  }, {
    id: 'revenue',
    label: 'Revenue'
  }];
  return <div className="min-h-screen bg-[#0D0D0D] text-white font-['Montserrat']" data-id="element-931">
      {/* Top Bar */}
      <header className="bg-[#0D0D0D]/90 backdrop-blur-xl border-b border-white/5 px-6 py-3 sticky top-0 z-50" data-id="element-932">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between" data-id="element-933">
          <div className="flex items-center gap-4" data-id="element-934">
            {onBack && <button onClick={onBack} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-id="element-935">
                <ArrowLeft size={20} className="text-gray-400" data-id="element-936" />
              </button>}
            <div className="flex items-center gap-2" data-id="element-937">
              <div className="w-8 h-8 bg-[#FFD700] rounded flex items-center justify-center" data-id="element-938">
                <span className="text-black font-['Archivo_Black'] text-lg" data-id="element-939">
                  H
                </span>
              </div>
              <span className="font-['Archivo_Black'] text-lg tracking-tighter uppercase" data-id="element-940">
                Creator Dashboard
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-941">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg" data-id="element-942">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-943"></div>
              <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest" data-id="element-944">
                All Systems Online
              </span>
            </div>
            <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest hover:bg-white transition-all" data-id="element-945">
              Upgrade Plan
            </button>
          </div>
        </div>
      </header>

      {/* Section Tabs */}
      <div className="border-b border-white/5 px-6 bg-[#0D0D0D]/50 backdrop-blur-sm sticky top-[57px] z-40" data-id="element-946">
        <div className="max-w-[1800px] mx-auto flex gap-1 overflow-x-auto py-2" data-id="element-947">
          {sections.map(s => <button key={s.id} onClick={() => setActiveSection(s.id)} className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest rounded-lg whitespace-nowrap transition-all ${activeSection === s.id ? 'bg-[#FFD700] text-black' : 'text-gray-500 hover:text-white hover:bg-white/5'}`} data-id="element-948">
              {s.label}
            </button>)}
        </div>
      </div>

      <main className="max-w-[1800px] mx-auto p-6 space-y-6" data-id="element-949">
        {/* Overview Section */}
        {activeSection === 'overview' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-950">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-951">
              {[{
            label: 'Monthly Visitors',
            value: '48.2K',
            change: '+12.4%',
            icon: Eye,
            color: 'text-[#FFD700]'
          }, {
            label: 'Stream Revenue',
            value: '$4,120',
            change: '+24.1%',
            icon: Play,
            color: 'text-emerald-500'
          }, {
            label: 'Store Sales',
            value: '$2,840',
            change: '+8.7%',
            icon: ShoppingBag,
            color: 'text-blue-500'
          }, {
            label: 'Email Subscribers',
            value: '12.4K',
            change: '+340',
            icon: Mail,
            color: 'text-purple-500'
          }].map((stat, i) => <div key={i} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-952">
                  <div className="flex items-center justify-between mb-4" data-id="element-953">
                    <stat.icon size={20} className={stat.color} data-id="element-954" />
                    <span className="text-[10px] font-bold text-emerald-500 uppercase" data-id="element-955">
                      {stat.change}
                    </span>
                  </div>
                  <p className="text-2xl font-['Archivo_Black'] tracking-tight" data-id="element-956">
                    {stat.value}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-957">
                    {stat.label}
                  </p>
                </div>)}
            </div>

            {/* Traffic + Revenue Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-958">
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-959">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-960">
                  Content Traffic (7 Days)
                </h3>
                <div className="h-[250px]" data-id="element-961">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-962">
                    <AreaChart data={trafficData} data-id="element-963">
                      <defs data-id="element-964">
                        <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-965">
                          <stop offset="5%" stopColor="#FFD700" stopOpacity={0.2} data-id="element-966" />
                          <stop offset="95%" stopColor="#FFD700" stopOpacity={0} data-id="element-967" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-968" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-969" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-970" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }} data-id="element-971" />
                      <Area type="monotone" dataKey="views" stroke="#FFD700" strokeWidth={2} fill="url(#goldGrad)" data-id="element-972" />
                      <Area type="monotone" dataKey="streams" stroke="#FF0000" strokeWidth={2} fill="transparent" strokeDasharray="5 5" data-id="element-973" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-974">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-975">
                  Revenue Breakdown (6 Months)
                </h3>
                <div className="h-[250px]" data-id="element-976">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-977">
                    <BarChart data={revenueData} data-id="element-978">
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-979" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-980" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-981" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '11px'
                  }} data-id="element-982" />
                      <Bar dataKey="streaming" fill="#FFD700" radius={[4, 4, 0, 0]} data-id="element-983" />
                      <Bar dataKey="store" fill="#FF0000" radius={[4, 4, 0, 0]} data-id="element-984" />
                      <Bar dataKey="ads" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-985" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex gap-6 mt-4" data-id="element-986">
                  {[{
                label: 'Streaming',
                color: 'bg-[#FFD700]'
              }, {
                label: 'Store',
                color: 'bg-[#FF0000]'
              }, {
                label: 'Ads',
                color: 'bg-[#4F46E5]'
              }].map((l, i) => <div key={i} className="flex items-center gap-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-987">
                      <div className={`w-2 h-2 rounded-full ${l.color}`} data-id="element-988"></div>
                      {l.label}
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Hosting & Domains */}
        {activeSection === 'hosting' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-989">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-990">
              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-991">
                <div className="flex items-center gap-3 mb-6" data-id="element-992">
                  <Server size={20} className="text-[#FFD700]" data-id="element-993" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-994">
                    Hosting Status
                  </h3>
                </div>
                <div className="space-y-4" data-id="element-995">
                  {[{
                label: 'Plan',
                value: 'Creator Pro ($149/mo)'
              }, {
                label: 'Storage',
                value: '45 GB / 100 GB'
              }, {
                label: 'Bandwidth',
                value: '820 GB / Unlimited'
              }, {
                label: 'Uptime',
                value: '99.99%'
              }].map((item, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-996">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-997">
                        {item.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-998">{item.value}</span>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-999">
                <div className="flex items-center gap-3 mb-6" data-id="element-1000">
                  <Globe size={20} className="text-[#FFD700]" data-id="element-1001" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1002">
                    Domains
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-1003">
                  {[{
                domain: 'artistname.com',
                status: 'Active',
                ssl: true
              }, {
                domain: 'artistname.store',
                status: 'Active',
                ssl: true
              }, {
                domain: 'artistname.live',
                status: 'Pending',
                ssl: false
              }].map((d, i) => <div key={i} className="flex items-center justify-between p-3 bg-black/40 rounded-lg border border-white/5" data-id="element-1004">
                      <div data-id="element-1005">
                        <p className="text-sm font-bold" data-id="element-1006">{d.domain}</p>
                        <div className="flex items-center gap-2 mt-1" data-id="element-1007">
                          {d.ssl && <span className="text-[8px] font-bold text-emerald-500 bg-emerald-500/10 px-1.5 py-0.5 rounded uppercase" data-id="element-1008">
                              SSL+
                            </span>}
                          <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${d.status === 'Active' ? 'text-emerald-500 bg-emerald-500/10' : 'text-amber-500 bg-amber-500/10'}`} data-id="element-1009">
                            {d.status}
                          </span>
                        </div>
                      </div>
                      <ExternalLink size={14} className="text-gray-600" data-id="element-1010" />
                    </div>)}
                </div>
                <button className="mt-4 w-full py-2.5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors flex items-center justify-center gap-2" data-id="element-1011">
                  <Plus size={14} data-id="element-1012" /> Add Domain
                </button>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1013">
                <div className="flex items-center gap-3 mb-6" data-id="element-1014">
                  <Shield size={20} className="text-emerald-500" data-id="element-1015" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1016">
                    SSL & Security
                  </h3>
                </div>
                <div className="text-center py-8" data-id="element-1017">
                  <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4" data-id="element-1018">
                    <Shield size={32} className="text-emerald-500" data-id="element-1019" />
                  </div>
                  <p className="text-2xl font-['Archivo_Black']" data-id="element-1020">SSL+ Active</p>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-2" data-id="element-1021">
                    Wildcard Certificate
                  </p>
                  <p className="text-[10px] text-gray-600 mt-1" data-id="element-1022">
                    Expires: Dec 2025
                  </p>
                </div>
                <div className="space-y-2 mt-4" data-id="element-1023">
                  {['DDoS Protection', 'Firewall Active', 'Daily Backups'].map((f, i) => <div key={i} className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1024">
                        <CheckCircle2 size={14} className="text-emerald-500" data-id="element-1025" />
                        {f}
                      </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Streaming */}
        {activeSection === 'streaming' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1026">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1027">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1028">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1029">
                  Monetization Tracking
                </h3>
                <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-1030">
                  {[{
                label: 'Total Streams',
                value: '284K',
                sub: 'This month'
              }, {
                label: 'Revenue',
                value: '$4,120',
                sub: '+24% vs last month'
              }, {
                label: 'Avg. Per Stream',
                value: '$0.0145',
                sub: 'Above industry avg'
              }].map((s, i) => <div key={i} className="bg-black/40 rounded-lg p-4 border border-white/5" data-id="element-1031">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-1032">
                        {s.label}
                      </p>
                      <p className="text-xl font-['Archivo_Black'] mt-1" data-id="element-1033">
                        {s.value}
                      </p>
                      <p className="text-[10px] text-gray-600 mt-1" data-id="element-1034">{s.sub}</p>
                    </div>)}
                </div>
                <div className="h-[200px]" data-id="element-1035">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1036">
                    <LineChart data={trafficData} data-id="element-1037">
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1038" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1039" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fontSize: 10,
                    fill: '#555'
                  }} data-id="element-1040" />
                      <Tooltip contentStyle={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #333',
                    borderRadius: '8px'
                  }} data-id="element-1041" />
                      <Line type="monotone" dataKey="streams" stroke="#FFD700" strokeWidth={3} dot={false} data-id="element-1042" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1043">
                <div className="flex items-center gap-3 mb-6" data-id="element-1044">
                  <Radio size={20} className="text-[#FF0000]" data-id="element-1045" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1046">
                    Live Stream
                  </h3>
                </div>
                <div className="text-center py-8" data-id="element-1047">
                  <div className="w-16 h-16 rounded-full bg-[#FF0000]/20 border border-[#FF0000]/30 flex items-center justify-center mx-auto mb-4" data-id="element-1048">
                    <Play size={24} className="text-[#FF0000] ml-0.5" data-id="element-1049" />
                  </div>
                  <p className="font-bold" data-id="element-1050">Ready to Go Live</p>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1051">
                    Wowza Integration Active
                  </p>
                </div>
                <button className="w-full py-3 bg-[#FF0000] text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-red-700 transition-colors" data-id="element-1052">
                  Start Live Stream
                </button>
              </div>
            </div>
          </motion.div>}

        {/* Content & Traffic */}
        {activeSection === 'content' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1053">
            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1054">
              <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1055">
                Content Performance & Traffic Sources
              </h3>
              <div className="h-[300px]" data-id="element-1056">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1057">
                  <AreaChart data={trafficData} data-id="element-1058">
                    <defs data-id="element-1059">
                      <linearGradient id="goldGrad2" x1="0" y1="0" x2="0" y2="1" data-id="element-1060">
                        <stop offset="5%" stopColor="#FFD700" stopOpacity={0.3} data-id="element-1061" />
                        <stop offset="95%" stopColor="#FFD700" stopOpacity={0} data-id="element-1062" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1063" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1064" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1065" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }} data-id="element-1066" />
                    <Area type="monotone" dataKey="views" stroke="#FFD700" strokeWidth={2} fill="url(#goldGrad2)" name="Page Views" data-id="element-1067" />
                    <Area type="monotone" dataKey="streams" stroke="#FF0000" strokeWidth={2} fill="transparent" strokeDasharray="5 5" name="Stream Views" data-id="element-1068" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1069">
              {[{
            source: 'Direct',
            visits: '18.2K',
            pct: '38%'
          }, {
            source: 'Social Media',
            visits: '12.4K',
            pct: '26%'
          }, {
            source: 'Search (SEO)',
            visits: '10.1K',
            pct: '21%'
          }, {
            source: 'Referral',
            visits: '7.5K',
            pct: '15%'
          }].map((s, i) => <div key={i} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-id="element-1070">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest" data-id="element-1071">
                    {s.source}
                  </p>
                  <p className="text-xl font-['Archivo_Black'] mt-1" data-id="element-1072">
                    {s.visits}
                  </p>
                  <p className="text-xs text-[#FFD700] font-bold mt-1" data-id="element-1073">
                    {s.pct} of total
                  </p>
                </div>)}
            </div>
          </motion.div>}

        {/* Email & CRM */}
        {activeSection === 'email' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1074">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1075">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1076">
                <div className="flex items-center justify-between mb-6" data-id="element-1077">
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1078">
                    Email Campaigns
                  </h3>
                  <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest" data-id="element-1079">
                    <Plus size={14} className="inline mr-1" data-id="element-1080" /> New Campaign
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-1081">
                  {emailStats.map((s, i) => <div key={i} className="bg-black/40 rounded-lg p-4 border border-white/5 text-center" data-id="element-1082">
                      <p className="text-2xl font-['Archivo_Black']" data-id="element-1083">
                        {(s.value / 1000).toFixed(1)}K
                      </p>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-1084">
                        {s.name}
                      </p>
                    </div>)}
                </div>
                <div className="space-y-3" data-id="element-1085">
                  {[{
                subject: 'New Single Drop — Listen Now 🎵',
                sent: '12.4K',
                opened: '67%',
                date: 'Oct 12'
              }, {
                subject: 'Tour Dates Announced!',
                sent: '11.8K',
                opened: '72%',
                date: 'Oct 5'
              }, {
                subject: 'Exclusive Merch Pre-Sale',
                sent: '10.2K',
                opened: '58%',
                date: 'Sep 28'
              }].map((c, i) => <div key={i} className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-white/5" data-id="element-1086">
                      <div data-id="element-1087">
                        <p className="text-sm font-bold" data-id="element-1088">{c.subject}</p>
                        <p className="text-[10px] text-gray-500 font-bold mt-1" data-id="element-1089">
                          {c.date} • Sent to {c.sent}
                        </p>
                      </div>
                      <span className="text-xs font-bold text-[#FFD700]" data-id="element-1090">
                        {c.opened} open rate
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1091">
                <div className="flex items-center gap-3 mb-6" data-id="element-1092">
                  <Users size={20} className="text-purple-500" data-id="element-1093" />
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1094">CRM</h3>
                </div>
                <div className="space-y-4" data-id="element-1095">
                  {[{
                label: 'Total Contacts',
                value: '12,400'
              }, {
                label: 'VIP Fans',
                value: '842'
              }, {
                label: 'New This Month',
                value: '+340'
              }, {
                label: 'Engagement Rate',
                value: '67%'
              }].map((s, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-1096">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-1097">
                        {s.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-1098">{s.value}</span>
                    </div>)}
                </div>
                <button className="mt-6 w-full py-2.5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors" data-id="element-1099">
                  Manage Contacts
                </button>
              </div>
            </div>
          </motion.div>}

        {/* Social Scheduler */}
        {activeSection === 'social' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1100">
            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1101">
              <div className="flex items-center justify-between mb-6" data-id="element-1102">
                <h3 className="font-bold uppercase tracking-wider" data-id="element-1103">
                  Social Media Post Scheduler
                </h3>
                <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-1104">
                  <Plus size={14} data-id="element-1105" /> Schedule Post
                </button>
              </div>
              <div className="space-y-3" data-id="element-1106">
                {scheduledPosts.map((post, i) => <div key={i} className="flex items-center justify-between p-4 bg-black/40 rounded-lg border border-white/5 hover:border-[#FFD700]/20 transition-colors" data-id="element-1107">
                    <div className="flex items-center gap-4" data-id="element-1108">
                      <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center" data-id="element-1109">
                        <span className="text-[10px] font-bold text-gray-400 uppercase" data-id="element-1110">
                          {post.platform.slice(0, 2)}
                        </span>
                      </div>
                      <div data-id="element-1111">
                        <p className="text-sm font-bold" data-id="element-1112">{post.content}</p>
                        <div className="flex items-center gap-2 mt-1" data-id="element-1113">
                          <span className="text-[10px] font-bold text-gray-500" data-id="element-1114">
                            {post.platform}
                          </span>
                          <span className="text-[10px] text-gray-600" data-id="element-1115">•</span>
                          <Clock size={10} className="text-gray-600" data-id="element-1116" />
                          <span className="text-[10px] text-gray-500" data-id="element-1117">
                            {post.time}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${post.status === 'scheduled' ? 'bg-[#FFD700]/10 text-[#FFD700]' : 'bg-gray-800 text-gray-500'}`} data-id="element-1118">
                      {post.status}
                    </span>
                  </div>)}
              </div>
            </div>
          </motion.div>}

        {/* E-Commerce */}
        {activeSection === 'store' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1119">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1120">
              <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1121">
                <div className="flex items-center justify-between mb-6" data-id="element-1122">
                  <h3 className="font-bold uppercase tracking-wider" data-id="element-1123">
                    Store Products
                  </h3>
                  <button className="bg-[#FFD700] text-black px-4 py-2 rounded font-bold text-[10px] uppercase tracking-widest flex items-center gap-2" data-id="element-1124">
                    <Plus size={14} data-id="element-1125" /> Add Product
                  </button>
                </div>
                <table className="w-full" data-id="element-1126">
                  <thead data-id="element-1127">
                    <tr className="text-[10px] font-bold text-gray-500 uppercase tracking-widest border-b border-white/5" data-id="element-1128">
                      <th className="text-left py-3" data-id="element-1129">Product</th>
                      <th className="text-left py-3" data-id="element-1130">Price</th>
                      <th className="text-left py-3" data-id="element-1131">Sold</th>
                      <th className="text-left py-3" data-id="element-1132">Stock</th>
                      <th className="text-right py-3" data-id="element-1133">Revenue</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1134">
                    {storeProducts.map((p, i) => <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors" data-id="element-1135">
                        <td className="py-4 text-sm font-bold" data-id="element-1136">{p.name}</td>
                        <td className="py-4 text-sm text-gray-400" data-id="element-1137">
                          ${p.price}
                        </td>
                        <td className="py-4 text-sm text-gray-400" data-id="element-1138">{p.sold}</td>
                        <td className="py-4" data-id="element-1139">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.stock < 15 ? 'bg-[#FF0000]/10 text-[#FF0000]' : 'bg-emerald-500/10 text-emerald-500'}`} data-id="element-1140">
                            {p.stock} left
                          </span>
                        </td>
                        <td className="py-4 text-sm font-bold text-right text-[#FFD700]" data-id="element-1141">
                          ${(p.price * p.sold).toLocaleString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1142">
                <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1143">
                  Store Stats
                </h3>
                <div className="space-y-4" data-id="element-1144">
                  {[{
                label: 'Total Revenue',
                value: '$18,240'
              }, {
                label: 'Orders This Month',
                value: '255'
              }, {
                label: 'Avg Order Value',
                value: '$71.50'
              }, {
                label: 'Conversion Rate',
                value: '3.2%'
              }].map((s, i) => <div key={i} className="flex justify-between py-3 border-b border-white/5 last:border-0" data-id="element-1145">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-1146">
                        {s.label}
                      </span>
                      <span className="text-sm font-bold" data-id="element-1147">{s.value}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>}

        {/* Revenue */}
        {activeSection === 'revenue' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="space-y-6" data-id="element-1148">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1149">
              {[{
            label: 'Streaming',
            value: '$4,120',
            icon: Music
          }, {
            label: 'Storefront',
            value: '$2,840',
            icon: ShoppingBag
          }, {
            label: 'Ad Revenue',
            value: '$1,200',
            icon: BarChart3
          }, {
            label: 'Total Revenue',
            value: '$8,160',
            icon: DollarSign
          }].map((s, i) => <div key={i} className={`border border-white/5 rounded-xl p-6 ${i === 3 ? 'bg-[#FFD700]/10 border-[#FFD700]/20' : 'bg-[#1A1A1A]'}`} data-id="element-1150">
                  <s.icon size={20} className={i === 3 ? 'text-[#FFD700]' : 'text-gray-500'} data-id="element-1151" />
                  <p className="text-2xl font-['Archivo_Black'] mt-3" data-id="element-1152">
                    {s.value}
                  </p>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1" data-id="element-1153">
                    {s.label}
                  </p>
                </div>)}
            </div>

            <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-6" data-id="element-1154">
              <h3 className="font-bold uppercase tracking-wider mb-6" data-id="element-1155">
                Revenue Over Time
              </h3>
              <div className="h-[300px]" data-id="element-1156">
                <ResponsiveContainer width="100%" height="100%" data-id="element-1157">
                  <BarChart data={revenueData} data-id="element-1158">
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-1159" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1160" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 10,
                  fill: '#555'
                }} data-id="element-1161" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #333',
                  borderRadius: '8px'
                }} data-id="element-1162" />
                    <Bar dataKey="streaming" stackId="a" fill="#FFD700" radius={[0, 0, 0, 0]} data-id="element-1163" />
                    <Bar dataKey="store" stackId="a" fill="#FF0000" data-id="element-1164" />
                    <Bar dataKey="ads" stackId="a" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-1165" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>}
      </main>
    </div>;
}